import React, { FC, useCallback, useMemo, useState } from 'react';

import dlv from 'dlv';
import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import {
  useBankMyRewardsTierId,
  useLoyaltyCategories,
} from 'components/app-header/loyalty/rewards-management/hooks';
import BlockRenderer from 'components/sanity-block-renderer';
import { useSelectedRewardTier } from 'hooks/loyalty/use-selected-reward';
import useErrorModal from 'hooks/use-error-modal';
import { useCustomizableContent } from 'state/customizable-content';
import { ILoyaltyCategory } from 'state/loyalty/types';
import { primitive } from 'styles/constants/primitives';

import { SectionDescription, SectionHeader, SectionHeaderWrapper } from '../styled';

import { BankMyRewardsLabel } from './components/bank-my-rewards-label';
import { CategoryDropdown } from './components/category-dropdown/category-dropdown';
import { RewardTierInput } from './components/reward-tier';
import { CategoryContainer, CategoryTitle, Fieldset, HiddenLegend } from './components/styled';
import { Check } from './icons/check';
import { RewardsStyleVariant, WithStyleVariant } from './types';

const RewardCategoryContainerWrapper = styled.div<{ bottomMargin: boolean }>`
  margin-bottom: ${({ bottomMargin }) => bottomMargin && primitive.$spacing3};
`;

export const RewardTierOptionContainer = styled.div<
  { hideBorder?: boolean; standalone?: boolean } & WithStyleVariant
>`
  border-bottom-width: ${props => (props.hideBorder ? `0px` : `1px`)};
  border-bottom-style: solid;
  border-bottom-color: ${props =>
    props.variant === RewardsStyleVariant.Secondary
      ? primitive.$blackOpacity30
      : props.theme.token('border-color-default')};
  margin: 0rem;
  padding: ${primitive.$spacing2} 0rem ${primitive.$spacing2} 0rem;

  cursor: pointer;
`;

const CheckmarkWrapper = styled.span`
  margin-left: ${primitive.$spacing2};
  position: relative;
  bottom: 1px;
`;

type IRewardCategoriesProps = {
  radioButtonName: string; // in case there are multiple instances on page
  setSelectedRewardsTier: (id: string) => void;
} & WithStyleVariant;

type IRewardCategoryProps = Pick<IRewardCategoriesProps, 'radioButtonName'> & {
  category: ILoyaltyCategory;
  onChange: (id: string) => void;
  optionsVisible: boolean;
  selectedRewardTierId?: string | null;
  selectedCategory: boolean;
  toggleOptionsVisible: () => void;
} & WithStyleVariant;

const RewardCategory: FC<IRewardCategoryProps> = ({
  category: { _id: categoryId, name: categoryName, options: tiers },
  onChange,
  optionsVisible,
  radioButtonName,
  selectedRewardTierId,
  selectedCategory,
  toggleOptionsVisible,
  variant,
}) => {
  const selectedOptionName = useMemo(() => {
    if (!selectedCategory) {
      return undefined;
    }

    const option = tiers.find(
      ({ _id: tierId, name: tierName }) => tierId === selectedRewardTierId && tierName
    );

    return option?.name?.locale;
  }, [selectedCategory, selectedRewardTierId, tiers]);

  const theme = useTheme();

  return (
    <CategoryDropdown
      focusOptionsWhenNotVisible
      id={categoryId}
      label={categoryName?.locale}
      onFocus={() => !optionsVisible && toggleOptionsVisible()}
      optionsVisible={optionsVisible}
      selectedOptionName={selectedOptionName}
      title={
        <CategoryTitle variant={variant}>
          {categoryName?.locale}
          {selectedCategory && (
            <CheckmarkWrapper>
              <Check
                fillColor={
                  variant === RewardsStyleVariant.Secondary
                    ? theme.token('icon-reversed')
                    : theme.token('icon-active')
                }
              />
            </CheckmarkWrapper>
          )}
        </CategoryTitle>
      }
      toggleOptionsVisible={toggleOptionsVisible}
      variant={variant}
    >
      {tiers.map(({ _id: tierId, name: tierName, points }, index) => (
        <RewardTierOptionContainer
          key={tierId}
          hideBorder={index === tiers.length - 1}
          onClick={() => onChange(tierId)}
          variant={variant}
        >
          <RewardTierInput
            allowTabNavigation={optionsVisible}
            name={tierName?.locale}
            radioButtonName={radioButtonName}
            selected={selectedCategory && tierId === selectedRewardTierId}
            tierLabel={points}
            value={tierId}
          />
        </RewardTierOptionContainer>
      ))}
    </CategoryDropdown>
  );
};

const BANK_MY_REWARDS_CATEGORY_INDEX = -1;
const sectionHeaderId = 'reward-categories-section-header';
const sectionDescriptionId = 'reward-categories-section-description';

const RewardCategories: FC<IRewardCategoriesProps> = ({
  radioButtonName,
  setSelectedRewardsTier,
  variant = RewardsStyleVariant.Primary,
}) => {
  const loyaltyCategories = useLoyaltyCategories();
  const { formatMessage } = useIntl();
  const { loyaltyContent } = useCustomizableContent();

  const [openCategoryAccordionIndex, setOpenCategoryAccordionIndex] = useState(-1);
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Setting Reward Tier Failure',
  });
  const onSetRewardsTierError = useCallback(
    () => openErrorDialog({ message: formatMessage({ id: 'setRewardsTierError' }) }),
    [openErrorDialog, formatMessage]
  );

  const { bankMyRewardTierId } = useBankMyRewardsTierId();
  const {
    onChangeRewardTier,
    selectedRewardCategoryId,
    selectedRewardTierId,
  } = useSelectedRewardTier(loyaltyCategories, setSelectedRewardsTier, { onSetRewardsTierError });

  return (
    <>
      <SectionHeaderWrapper data-testid="reward-categories-header-wrapper">
        <SectionHeader id={sectionHeaderId} variant={variant}>
          {dlv(loyaltyContent, 'rewardSetupPageText.locale')}
        </SectionHeader>
        <SectionDescription id={sectionDescriptionId}>
          <BlockRenderer content={dlv(loyaltyContent, 'rewardSetupPageBlockText.localeRaw')} />
        </SectionDescription>
      </SectionHeaderWrapper>

      <Fieldset>
        <HiddenLegend aria-labelledby={sectionHeaderId} aria-describedby={sectionDescriptionId} />
        <RewardCategoryContainerWrapper
          bottomMargin={Boolean(loyaltyCategories && loyaltyCategories.length)}
        >
          <CategoryContainer>
            <RewardTierOptionContainer
              hideBorder
              standalone
              onClick={() => onChangeRewardTier(bankMyRewardTierId)}
              variant={variant}
            >
              <RewardTierInput
                name={formatMessage({ id: 'bankMyRewards' })}
                radioButtonName={radioButtonName}
                selected={selectedRewardTierId === bankMyRewardTierId}
                standalone
                tierLabel={<BankMyRewardsLabel variant={variant} />}
                value={bankMyRewardTierId || ''}
                variant={variant}
              />
            </RewardTierOptionContainer>
          </CategoryContainer>
        </RewardCategoryContainerWrapper>
        {loyaltyCategories.map((cat: ILoyaltyCategory, index: number) => (
          <RewardCategoryContainerWrapper
            key={index}
            bottomMargin={index !== loyaltyCategories.length - 1}
          >
            <RewardCategory
              category={cat}
              onChange={onChangeRewardTier}
              optionsVisible={index === openCategoryAccordionIndex}
              radioButtonName={radioButtonName}
              selectedRewardTierId={selectedRewardTierId}
              selectedCategory={cat._id === selectedRewardCategoryId}
              variant={variant}
              toggleOptionsVisible={() =>
                index === openCategoryAccordionIndex
                  ? setOpenCategoryAccordionIndex(BANK_MY_REWARDS_CATEGORY_INDEX)
                  : setOpenCategoryAccordionIndex(index)
              }
            />
          </RewardCategoryContainerWrapper>
        ))}
      </Fieldset>
      <ErrorDialog />
    </>
  );
};

export default RewardCategories;
